import type { FlightError } from "@b2bportal/air-booking-api";
import type { PurchaseErrorEnum } from "@b2bportal/purchase-api";

import type { SeatSelectionErrorModal } from "../../types/common";

/**
 * Actions that can be triggered by error modal buttons
 */
export enum FlightErrorAction {
  /** Navigate to flight search page */
  NavigateToFlightSearch = "NavigateToFlightSearch",
  /** Navigate to My Trips page */
  NavigateToMyTrips = "NavigateToMyTrips",
  /** Navigate to booking confirmation page */
  NavigateToConfirmation = "NavigateToConfirmation",
  /** Open passenger picker/selector */
  OpenPassengerPicker = "OpenPassengerPicker",
  /** Go back to passenger info form */
  GoBackToPassengerInfo = "GoBackToPassengerInfo",
  /** Open passport form specifically */
  OpenPassportForm = "OpenPassportForm",
  /** Retry the quote operation */
  RetryQuote = "RetryQuote",
  /** Clear error and close modal */
  ClearError = "ClearError",
  /** No action (empty button) */
  None = "None",
  /** Continue checkout without seat selection */
  ContinueWithoutSeats = "ContinueWithoutSeats",
}

/**
 * Button configuration for error modals
 */
export interface FlightErrorButton {
  /** Translation key for button text */
  textKey: string;
  /** Action to perform when button is clicked */
  action: FlightErrorAction;
}

/**
 * Configuration for a single flight error
 */
export interface FlightErrorConfig {
  /** Translation key for error title */
  titleKey: string;
  /** Translation key for error subtitle */
  subtitleKey: string;
  /** Primary button configuration */
  primaryButton: FlightErrorButton;
  /** Secondary button configuration (optional) */
  secondaryButton?: FlightErrorButton;
  /** Optional CSS class for styling */
  className?: string;
}

/**
 * Context in which the error occurs
 */
export type FlightErrorContext = "CART_QUOTE" | "CART_FULFILL";

/**
 * Complete error configuration map by context and error code
 */
export type FlightErrorConfigMap = {
  [context in FlightErrorContext]: {
    [errorCode in FlightError]?: FlightErrorConfig;
  } & {
    /** Default fallback configuration */
    default: FlightErrorConfig;
  };
};

/**
 * Purchase-level error configuration (for errors that aren't FlightError specific)
 */
export type PurchaseErrorConfigMap = {
  [context in FlightErrorContext]: {
    [category in PurchaseErrorEnum]?: FlightErrorConfig;
  };
};

/**
 * Seat selection error configuration map
 */
export type SeatSelectionErrorConfigMap = {
  [errorCode in SeatSelectionErrorModal]: FlightErrorConfig;
} & {
  /** Default fallback configuration */
  default: FlightErrorConfig;
};
